import { ReactNode } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Loader2, Sparkles, Lock } from "lucide-react";
import { useIsStaff } from "@/hooks/useStaffRoles";
import { usePlanFeature } from "@/hooks/usePlanFeature";
import { tr } from "@/lib/tr";

interface Props {
  feature: string;
  children: ReactNode;
}

/**
 * Wraps a route/section so it only renders for workspaces on a plan that
 * includes `feature`. Staff bypass the gate. Denied users see an upgrade CTA.
 */
export const PlanFeatureGate = ({ feature, children }: Props) => {
  const { isStaff } = useIsStaff();
  const allowed = usePlanFeature(feature);
  useTranslation(); // ensure re-render on language change

  if (isStaff) return <>{children}</>;
  if (allowed === undefined) {
    return (
      <div className="flex items-center justify-center py-16">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }
  if (allowed) return <>{children}</>;

  return (
    <div className="min-h-[calc(100vh-200px)] flex items-center justify-center p-6">
      <Card className="max-w-xl w-full border-primary/30">
        <CardContent className="pt-8 pb-6 text-center space-y-5">
          <div className="mx-auto w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center">
            <Lock className="h-8 w-8 text-primary" />
          </div>
          <h1 className="text-2xl font-bold font-display">
            {tr(
              "هذه الميزة متاحة في الباقة الاحترافية",
              "This feature is available on the Professional plan",
              "Cette fonctionnalité est disponible dans le forfait Professionnel"
            )}
          </h1>
          <p className="text-muted-foreground">
            {tr(
              "قم بالترقية للوصول إلى التقارير المتقدمة وتجميع بيانات الشركات وميزات إضافية.",
              "Upgrade to unlock advanced analytics, multi-company consolidation, and more.",
              "Passez au forfait supérieur pour accéder aux analyses avancées, à la consolidation multi-sociétés et plus encore."
            )}
          </p>
          <div className="flex flex-col sm:flex-row gap-2 justify-center pt-2">
            <Button asChild size="lg">
              <Link to="/billing/plans">
                <Sparkles className="me-2 h-4 w-4" />
                {tr("الترقية الآن", "Upgrade now", "Mettre à niveau")}
              </Link>
            </Button>
            <Button asChild variant="outline" size="lg">
              <Link to="/billing">
                {tr("تفاصيل الاشتراك", "Subscription details", "Détails de l'abonnement")}
              </Link>
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};
